import { Signal } from 'typed-signals';
import type { AdResponseValueType } from '../tracking/trackingModules/avo/Avo';
import { LoadingView } from '../../views/LoadingView';
import { soundManager } from '../SoundManager';
import type { AppRemoteConfig, PlatformAdModule } from '../platform/Platform';
import type { IObservableValue } from '../../mainMenu/types';
import { DEFAULT_REMOTE_CONFIG } from '../firebase/defaultRemoteConfig';
import {
  createInterstitialStrategy,
  type InterstitialSlotName,
  type InterstitialStrategy,
  type InterstitialStrategyContext,
  type InterstitialStrategyState,
} from './strategies';

export type { InterstitialSlotName };

interface AdsPlayerConfig {
  getAdModule: () => PlatformAdModule | null;
  getLevelNumber: () => number;
  isPayer: () => boolean;
  /** Get timestamp (ms) of last purchase, or null if no purchases */
  getLastPurchaseTimestamp?: () => number | null;
  /** Remote config. If null, uses legacy hardcoded logic. */
  remoteConfig?: AppRemoteConfig | null;
  isTier1: () => boolean;
  observable: IObservableValue<{
    lastInterstitialAdTime: number;
  }>;
  /** When true, skip win/loss and mid-level interstitials (the platform owns the ad timer). */
  disableAutomaticInterstitials?: () => boolean;
}

export class AdsPlayer {
  public readonly onInterstitialShown = new Signal();

  private readonly state: InterstitialStrategyState = {
    slotNameUseCounter: { win: 0, lost: 0 },
    lastInterstitialTimestamp: 0,
    sessionAdCount: 0,
  };

  private strategy: InterstitialStrategy;

  /** Active interstitial config (from remote config or default). Used for getInterstitialConfig(). */
  private remoteConfig: AppRemoteConfig;

  constructor(private readonly config: AdsPlayerConfig) {
    this.remoteConfig = config.remoteConfig ?? DEFAULT_REMOTE_CONFIG;
    const useRemoteConfigFlow =
      config.remoteConfig !== undefined && config.remoteConfig !== null ? !config.remoteConfig : false;
    this.strategy = createInterstitialStrategy(
      useRemoteConfigFlow,
      this.buildContext(),
      this.state,
      config.remoteConfig ?? undefined,
    );
  }

  private buildContext(): InterstitialStrategyContext {
    return {
      getLevelNumber: () => this.config.getLevelNumber(),
      isPayer: () => this.config.isPayer(),
      getLastPurchaseTimestamp: this.config.getLastPurchaseTimestamp,
      isTier1: () => this.config.isTier1(),
      getLastInterstitialAdTime: () => this.config.observable.getValue().lastInterstitialAdTime,
    };
  }

  /**
   * Update interstitial config (e.g., when remote config loads asynchronously)
   */
  public setInterstitialConfig(config: AppRemoteConfig): void {
    this.remoteConfig = config;
    const useRemoteConfigFlow = !config.ads.interstitials.useFacebookFlowForInterstitials;
    this.strategy = createInterstitialStrategy(useRemoteConfigFlow, this.buildContext(), this.state, config);
  }

  public async showInterstitialAd(slotName: InterstitialSlotName): Promise<void> {
    if (this.config.disableAutomaticInterstitials?.() ?? false) return;
    if (!this.strategy.canShowInterstitial(slotName)) return;

    const { getAdModule, observable } = this.config;

    const adModule = getAdModule();
    if (!adModule) return;

    try {
      void LoadingView.getInstance().show();
      const response = await adModule.showInterstitialAd(slotName);
      void LoadingView.getInstance().hide(response !== 'Error');

      // AZ-785: an interstitial suspends the WebAudio context. Try to resume it the
      // moment the ad closes/skips so music & SFX return without an extra tap; if the
      // browser blocks resume() here (no user-activation), SoundManager's persistent
      // pointerdown handler recovers audio on the next interaction.
      soundManager.playSounds();

      if (response !== 'Error') {
        this.strategy.recordInterstitialShown();
        this.onInterstitialShown.emit();
        observable.updateValue({
          lastInterstitialAdTime: Date.now(),
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  public async showRewardedAd(slotName: string): Promise<AdResponseValueType> {
    const { getAdModule } = this.config;

    const adModule = getAdModule();
    if (!adModule) return 'Error';

    try {
      void LoadingView.getInstance().show();
      const response = await adModule.showRewardedAd(slotName);
      void LoadingView.getInstance().hide(response !== 'Error');
      return response;
    } catch (e) {
      console.error(e);
      return 'Error';
    }
  }

  /**
   * Get the current interstitial config (for debugging/testing)
   */
  public getInterstitialConfig(): AppRemoteConfig {
    return this.remoteConfig;
  }

  /**
   * Get the current session ad count (for debugging/testing)
   */
  public getSessionAdCount(): number {
    return this.state.sessionAdCount;
  }

  /**
   * Check if mid-level interstitials are enabled and should be shown
   */
  public shouldShowMidLevelInterstitial(): boolean {
    if (this.config.disableAutomaticInterstitials?.() ?? false) return false;
    return this.strategy.shouldShowMidLevelInterstitial();
  }

  /**
   * Show mid-level interstitial ad if conditions are met
   * Returns true if ad was shown, false otherwise
   */
  public async showMidLevelInterstitialAd(): Promise<boolean> {
    if (!this.strategy.shouldShowMidLevelInterstitial()) return false;

    const { getAdModule } = this.config;

    const adModule = getAdModule();
    if (!adModule) return false;

    try {
      void LoadingView.getInstance().show();
      const response = await adModule.showInterstitialAd('mid');
      void LoadingView.getInstance().hide(response !== 'Error');

      if (response !== 'Error') {
        this.strategy.recordInterstitialShown();
        return true;
      }
    } catch (e) {
      console.error(e);
    }

    return false;
  }
}
